import React, { useEffect, useMemo, useRef } from "react";

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

export default function CommandBar({
  open,
  query,
  setQuery,
  onClose,
  actions = []
}) {
  const inputRef = useRef(null);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const navigable = useMemo(() => {
    return (actions || []).filter((a) => a && a.id && a.title && a.type !== "header" && typeof a.run === "function");
  }, [actions]);

  const filtered = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    const list = (actions || []).filter((a) => a && a.id && a.title);
    if (!q) return list;
    return list.filter((a) => {
      if (a.type === "header") return true;
      const hay = `${a.title} ${a.subtitle || ""} ${(a.keywords || []).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [actions, query]);

  const filteredNavigable = useMemo(() => {
    const ids = new Set(filtered.filter((a) => a?.type !== "header" && typeof a?.run === "function").map((a) => a.id));
    return navigable.filter((a) => ids.has(a.id)).slice(0, 16);
  }, [filtered, navigable]);

  useEffect(() => {
    if (!open) return;
    setActiveIndex(0);
    const t = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => clamp(i + 1, 0, Math.max(0, filteredNavigable.length - 1)));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => clamp(i - 1, 0, Math.max(0, filteredNavigable.length - 1)));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const act = filteredNavigable[activeIndex];
        if (act?.run) act.run();
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeIndex, filteredNavigable, onClose, open]);

  if (!open) return null;

  return (
    <div data-testid="commandbar" className="fixed inset-0 z-[9999] flex items-start justify-center pt-24">
      <button
        aria-label="Close command bar"
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={() => onClose?.()}
      />
      <div className="relative w-[min(740px,92vw)] rounded-3xl border border-white/18 bg-ink-900/92 text-white shadow-float overflow-hidden">
        <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-2xl bg-white/10 flex items-center justify-center text-sm font-semibold">
            ⌘K
          </div>
          <input
            data-testid="commandbar-input"
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery?.(e.target.value)}
            placeholder="Search folders, people, actions…"
            className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-white/50 focus-visible:ring-2 focus-visible:ring-accent-600/35 rounded-xl px-2 py-1 -mx-2"
          />
          <div className="text-[12px] text-white/60">Esc</div>
        </div>
        <div className="max-h-[420px] overflow-auto p-2">
          {filteredNavigable.length === 0 && filtered.filter((a) => a?.type === "header").length === 0 ? (
            <div className="p-6 text-white/70 text-sm">No matches.</div>
          ) : (
            <>
              {filtered.map((a) => {
                if (a.type === "header") {
                  return (
                    <div
                      key={a.id}
                      className="px-4 pt-3 pb-2 text-[11px] tracking-[0.18em] uppercase text-white/55"
                    >
                      {a.title}
                    </div>
                  );
                }
                const idx = filteredNavigable.findIndex((x) => x.id === a.id);
                const isActive = idx === activeIndex;
                const disabled = typeof a.run !== "function";
                return (
                  <button
                    key={a.id}
                    disabled={disabled}
                    onClick={() => {
                      a.run?.();
                      onClose?.();
                    }}
                    className={`w-full text-left px-4 py-3 rounded-2xl transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/30 ${
                      isActive ? "bg-white/12" : "hover:bg-white/7"
                    } ${disabled ? "opacity-60 cursor-default hover:bg-transparent" : ""}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 w-7 h-7 rounded-xl bg-white/10 flex items-center justify-center text-xs">
                        {a.icon || "→"}
                      </div>
                      <div className="flex-1">
                        <div className="text-[14px] font-semibold">{a.title}</div>
                        {a.subtitle ? <div className="text-[12px] text-white/65 mt-0.5">{a.subtitle}</div> : null}
                      </div>
                      {a.hint ? <div className="text-[12px] text-white/55">{a.hint}</div> : null}
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-white/10 text-[12px] text-white/60 flex items-center justify-between">
          <span>↑↓ to navigate • Enter to run</span>
          <span className="text-white/50">Premium Command Bar</span>
        </div>
      </div>
    </div>
  );
}

